import type { MutableRefObject, ReactNode } from "react"
import type { Root } from "react-dom/client"

// The quotation viewer renders React subtrees into the Apryse web component's shadow root via
// manual `createRoot` (the normal CSS/render pipeline doesn't reach the shadow DOM). These are
// the shared low-level primitives for owning those roots and their injected styles.

export type RootEntry = { root: Root; container: HTMLElement }

// Inject a stylesheet string into a shadow root. Styles imported with `?inline` are raw CSS
// strings and must be pushed into the shadow root by hand — see `core/shadowStyles.ts`.
export const injectStyles = (shadowRoot: ShadowRoot, css: string) => {
	const styleEl = document.createElement("style")
	styleEl.textContent = css
	shadowRoot.appendChild(styleEl)
}

// Unmount a shadow-DOM React root and clear its ref. Deferred to a microtask because React
// forbids unmounting a root synchronously from inside its own render/commit.
export const disposeRoot = (entryRef: MutableRefObject<RootEntry | null>) => {
	const entry = entryRef.current
	entryRef.current = null
	if (!entry) return
	queueMicrotask(() => {
		try {
			entry.root.unmount()
		} catch {
			/* root already torn down */
		}
	})
}

// Render into a shadow-DOM root guarded against a detached container (the Apryse UI can remove
// panel containers out from under us). Returns whether the render actually happened.
export const safeRender = (entryRef: MutableRefObject<RootEntry | null>, element: ReactNode): boolean => {
	const entry = entryRef.current
	if (!entry || !entry.container.isConnected) return false
	try {
		entry.root.render(element)
		return true
	} catch (error) {
		console.warn("WebViewer shadow render skipped:", error)
		return false
	}
}
